import React, { useState, useRef, useEffect } from 'react';
import Icon from '../Icon';

const SECTIONS = [
  {
    key: 'location',
    label: 'Location',
    title: 'Where do you live?',
    lede: "Programs differ by state — let's start there.",
    fields: [
      { id: 'state', label: 'What state do you live in?', kind: 'select', required: true,
        options: ['California', 'Arizona', 'Indiana', 'Missouri', 'Michigan', 'Other'] },
      { id: 'zip', label: 'What city or ZIP code are you in?', kind: 'text', required: true,
        placeholder: 'e.g. Sacramento or 95814' },
    ],
  },
  {
    key: 'recipient',
    label: 'Care recipient',
    title: 'Who are you caring for?',
    lede: 'Tell us a little about the person receiving care.',
    fields: [
      { id: 'relation', label: 'Who are you caring for?', kind: 'radio', required: true,
        options: ['Parent', 'Grandparent', 'Spouse', 'Child', 'Other family member', 'Friend'], cols: 3 },
      { id: 'age', label: 'How old is the person you care for?', kind: 'radio', required: true,
        options: ['Under 18', '18–64', '65+'], cols: 3 },
      { id: 'condition', label: 'Does the person have a medical condition or disability that requires help with daily activities?', kind: 'radio', required: true,
        options: ['Yes', 'No', 'Not sure'], cols: 3 },
    ],
  },
  {
    key: 'needs',
    label: 'Care needs',
    title: 'What kind of care do they need?',
    lede: 'This helps us match you to the right program.',
    fields: [
      { id: 'help', label: 'What type of help do they need?', kind: 'check', required: true,
        help: 'Select all that apply.', cols: 2,
        options: [
          { value: 'Bathing or hygiene', icon: 'sun' },
          { value: 'Dressing', icon: 'sprout' },
          { value: 'Cooking or meals', icon: 'utensils' },
          { value: 'Medication reminders', icon: 'pill' },
          { value: 'Mobility assistance', icon: 'walking' },
          { value: 'Memory supervision', icon: 'brain' },
          { value: 'Transportation', icon: 'car' },
          { value: 'Housekeeping', icon: 'broom' },
        ],
      },
      { id: 'hours', label: 'About how many hours per week do you provide care?', kind: 'radio', required: true,
        options: ['Less than 10 hours', '10–20 hours', '20–40 hours', '40+ hours'], cols: 4 },
      { id: 'duration', label: 'How long have you been providing care?', kind: 'radio', required: true,
        options: ['Less than 3 months', '3–6 months', '6–12 months', 'Over 1 year'], cols: 4 },
    ],
  },
  {
    key: 'qualification',
    label: 'Program qualification',
    title: 'A few quick qualification questions.',
    lede: "These rules vary — don't worry if you're not sure.",
    fields: [
      { id: 'medicaid', label: 'Is the person currently enrolled in Medicaid (Medi-Cal in California)?', kind: 'radio', required: true,
        options: ['Yes', 'No', 'Not sure'], cols: 3 },
      { id: 'income', label: 'What is the approximate monthly income of the person you care for?', kind: 'radio', required: true,
        options: ['Less than $1,500', '$1,500–$2,500', '$2,500–$4,000', 'Over $4,000', 'Not sure'], cols: 3 },
      { id: 'citizen', label: 'Is the person you care for a U.S. citizen or legal resident?', kind: 'radio', required: true,
        options: ['Yes', 'No', 'Not sure'], cols: 3 },
      { id: 'living', label: 'Where does the person you care for live?', kind: 'radio', required: true,
        options: ['In their own home', 'With family', 'Assisted living', 'Nursing home'], cols: 2 },
    ],
  },
  {
    key: 'intent',
    label: 'About you',
    title: 'Last step — how can we reach you?',
    lede: "We'll send your eligibility results within 24 hours.",
    fields: [
      { id: 'urgency', label: 'How soon are you looking to get paid for caregiving?', kind: 'radio', required: true,
        options: ['As soon as possible', 'Within the next 1–3 months', 'Just exploring options'], cols: 3 },
      { id: 'helpWanted', label: 'Would you like help with the application process if you qualify?', kind: 'radio', required: true,
        options: ['Yes, I want help', 'Maybe, depends on cost', 'No, just checking eligibility'], cols: 3 },
      { id: 'name', label: 'Full name', kind: 'text', required: true, placeholder: 'Maria Rodriguez' },
      { id: 'email', label: 'Email address', kind: 'email', required: true, placeholder: 'maria@example.com' },
      { id: 'phone', label: 'Phone number', kind: 'tel', required: false,
        help: 'Optional but strongly recommended — we get faster responses by phone.',
        placeholder: '(555) 123-4567' },
    ],
  },
];

const initialData = () => {
  const d = {};
  SECTIONS.forEach(s => s.fields.forEach(f => { d[f.id] = f.kind === 'check' ? [] : ''; }));
  return d;
};

const validateField = (field, value) => {
  if (!field.required) return null;
  if (field.kind === 'check') return (value && value.length > 0) ? null : 'Please select at least one.';
  if (!value || !String(value).trim()) return 'This is required.';
  if (field.kind === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter a valid email.';
  return null;
};

const RadioGroup = ({ field, value, onChange }) => (
  <div className={`options ${field.cols ? 'cols-' + field.cols : ''}`}>
    {field.options.map(opt => {
      const v = typeof opt === 'string' ? opt : opt.value;
      const selected = value === v;
      return (
        <label key={v} className={`option ${selected ? 'selected' : ''}`}>
          <input type="radio" name={field.id} value={v} checked={selected}
            onChange={() => onChange(v)} />
          <span className="indicator">
            <Icon name="check-bold" size={12} />
          </span>
          <span>{v}</span>
        </label>
      );
    })}
  </div>
);

const CheckGroup = ({ field, value, onChange }) => (
  <div className={`options ${field.cols ? 'cols-' + field.cols : ''}`}>
    {field.options.map(opt => {
      const v = typeof opt === 'string' ? opt : opt.value;
      const ic = typeof opt === 'object' ? opt.icon : null;
      const selected = (value || []).includes(v);
      return (
        <label key={v} className={`option ${selected ? 'selected' : ''}`}>
          <input type="checkbox" name={field.id} value={v} checked={selected}
            onChange={() => {
              const next = selected ? value.filter(x => x !== v) : [...(value || []), v];
              onChange(next);
            }} />
          <span className="indicator box">
            <Icon name="check-bold" size={12} />
          </span>
          {ic && <Icon name={ic} size={18} stroke={1.8} style={{ color: selected ? 'var(--primary)' : 'var(--ink-mute)' }} />}
          <span>{v}</span>
        </label>
      );
    })}
  </div>
);

const Field = ({ field, value, error, onChange }) => (
  <div className="field">
    <label className="field-label" htmlFor={field.id}>
      {field.label}{field.required && <span className="req">*</span>}
    </label>
    {field.help && <div className="field-help">{field.help}</div>}

    {field.kind === 'select' && (
      <select className="select" id={field.id} value={value} onChange={e => onChange(e.target.value)}>
        <option value="">Select your state…</option>
        {field.options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    )}
    {(field.kind === 'text' || field.kind === 'email' || field.kind === 'tel') && (
      <input className="input" id={field.id} type={field.kind}
        value={value} onChange={e => onChange(e.target.value)}
        placeholder={field.placeholder || ''} />
    )}
    {field.kind === 'radio' && <RadioGroup field={field} value={value} onChange={onChange} />}
    {field.kind === 'check' && <CheckGroup field={field} value={value} onChange={onChange} />}

    {error && <div className="field-error">{error}</div>}
  </div>
);

const EligibilityForm = () => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const cardRef = useRef(null);

  const section = SECTIONS[step];
  const total = SECTIONS.length;
  const progress = done ? 100 : ((step) / total) * 100;

  // Always start from a clean slate on page load — clear any stale localStorage entry.
  useEffect(() => {
    try { localStorage.removeItem('sc-form'); } catch (e) {}
  }, []);

  const setVal = (id) => (v) => {
    setData(d => ({ ...d, [id]: v }));
    setErrors(e => ({ ...e, [id]: null }));
  };

  const validateStep = () => {
    const errs = {};
    section.fields.forEach(f => {
      const e = validateField(f, data[f.id]);
      if (e) errs[f.id] = e;
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submitToWeb3Forms = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const fd = new FormData();
      fd.append('access_key', 'aa9a0b0d-6448-44ba-be92-d3249f15ace0');
      fd.append('subject', 'New SupportCare Eligibility Submission');
      fd.append('from_name', data.name || 'SupportCare Eligibility Form');
      // Walk every section/field so the email contains all 5 steps in order.
      SECTIONS.forEach(s => {
        s.fields.forEach(f => {
          const v = data[f.id];
          fd.append(f.id, Array.isArray(v) ? v.join(', ') : (v || ''));
        });
      });

      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: fd,
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Submission failed');
      return true;
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const next = async () => {
    if (submitting) return;
    if (!validateStep()) {
      cardRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      return;
    }
    if (step < total - 1) {
      setStep(s => s + 1);
      cardRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    } else {
      const ok = await submitToWeb3Forms();
      if (!ok) return;
      setDone(true);
      try { localStorage.removeItem('sc-form'); } catch (e) {}
    }
  };
  const back = () => {
    if (submitting) return;
    if (step > 0) setStep(s => s - 1);
    cardRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };
  const reset = () => {
    setDone(false); setStep(0); setData(initialData()); setErrors({}); setSubmitError('');
  };

  return (
    <section className="section-form" id="form">
      <div className="container">
        <div className="section-head center" style={{ marginBottom: 36 }}>
          <span className="eyebrow">2-minute check</span>
          <h2 className="section-title">
            See if you may qualify for <em style={{ color: 'var(--primary)', fontStyle: 'italic' }}>paid caregiving.</em>
          </h2>
        </div>

        <div className="form-card" ref={cardRef}>
          <div className="form-head">
            <h2>2-Minute Eligibility Check: Get Paid to Care for a Loved One</h2>
            <p>Answer a few quick questions to see if you may qualify for paid caregiving programs. We'll review your responses and reach out with next steps.</p>
            <div className="progress">
              <div className="progress-bar"><div className="progress-fill" style={{ width: progress + '%' }} /></div>
              <div className="progress-meta">
                <span>{done ? 'Complete' : `Step ${step + 1} of ${total}`} · <span className="step-name">{done ? 'All done' : section.label}</span></span>
                <span>{Math.round(progress)}% complete</span>
              </div>
            </div>
          </div>

          {!done ? (
            <>
              <div className="form-body fade-up" key={step}>
                <div className="form-step">
                  <div className="form-step-title">Section {step + 1} · {section.label}</div>
                  <h3>{section.title}</h3>
                  <p className="step-lede">{section.lede}</p>
                </div>

                {section.fields.map(f => (
                  <Field key={f.id} field={f}
                    value={data[f.id]} error={errors[f.id]}
                    onChange={setVal(f.id)} />
                ))}
              </div>

              <div className="form-footer">
                <span className="helper">
                  <Icon name="lock" size={14} stroke={2.2} />
                  Your answers are private and confidential.
                </span>
                <div className="actions">
                  {step > 0 && (
                    <button className="btn btn-ghost btn-sm" onClick={back} disabled={submitting}>
                      <Icon name="arrow-left" size={14} stroke={2.4} /> Back
                    </button>
                  )}
                  <button className="btn btn-primary btn-sm" onClick={next} disabled={submitting}>
                    {step === total - 1
                      ? (submitting ? 'Sending…' : 'See if I qualify')
                      : 'Continue'}
                    {!submitting && <Icon name="arrow-right" size={14} stroke={2.4} className="arrow" />}
                  </button>
                </div>
              </div>
              {submitError && (
                <div className="form-submit-error">
                  <Icon name="shield" size={13} stroke={2.2} />
                  {submitError}
                </div>
              )}
            </>
          ) : (
            <div className="form-complete fade-up">
              <div className="check-circle">
                <Icon name="check-bold" size={32} />
                <div className="sparkles" aria-hidden="true">
                  <span /><span /><span /><span /><span /><span /><span /><span />
                </div>
              </div>
              <h3>Thanks, {data.name?.split(' ')[0] || 'friend'} — your check is in.</h3>
              <p>
                Based on your answers, you may qualify for a paid caregiving program in {data.state || 'your state'}.
                A care advocate will review your responses and reach out within 24 hours
                {data.phone ? ` at ${data.phone}` : data.email ? ` at ${data.email}` : ''}.
              </p>
              <div className="next-steps">
                <div className="next-step">
                  <div className="ns-num">NEXT</div>
                  <div className="ns-text">We review your answers and identify programs.</div>
                </div>
                <div className="next-step">
                  <div className="ns-num">~24 HRS</div>
                  <div className="ns-text">An advocate calls you with your personalized plan.</div>
                </div>
                <div className="next-step">
                  <div className="ns-num">~14 DAYS</div>
                  <div className="ns-text">Average time from approval to first payment.</div>
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={reset} style={{ marginTop: 10 }}>
                Start over
              </button>
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: 22, fontSize: 13.5, color: 'var(--ink-mute)' }}>
          <Icon name="shield" size={13} stroke={2.2} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Protected by 256-bit SSL encryption. SupportCare is HIPAA-compliant.
        </div>
      </div>
    </section>
  );
};

export default EligibilityForm;
